import React, { useEffect, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Users, Activity, TrendingUp, AlertCircle } from 'lucide-react'
import type { UserStats, FunnelStats, AiPerformanceMetrics, ApiLatencyStats } from 'shared'

interface DashboardData {
  userStats: UserStats
  funnelStats: FunnelStats
  aiPerformance: AiPerformanceMetrics
  apiLatency: ApiLatencyStats
  lastUpdated: string
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      // 这里应该调用实际的API
      // const response = await fetch('/api/admin/dashboard')
      // const result = await response.json()
      
      // 模拟数据
      const mockData: DashboardData = {
        userStats: {
          dau: 156,
          wau: 892,
          mau: 3247,
          newRegistrations: 23,
          coreActivationRate: 0.68,
          totalUsers: 5432,
          proUsers: 1234,
          timeRange: {
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString()
          }
        },
        funnelStats: {
          photoTaken: 1250,
          aiRequestSent: 1180,
          aiResponseReceived: 1156,
          noteSaved: 892,
          conversionRates: {
            photoToRequest: 0.944,
            requestToResponse: 0.98,
            responseToSave: 0.772,
            overallConversion: 0.714
          },
          timeRange: {
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString()
          }
        },
        aiPerformance: {
          accuracyRate: 0.87,
          recallRate: 0.82,
          averageCorrectionCount: 2.3,
          totalPredictions: 15678,
          totalCorrections: 3456,
          commonMisidentifications: [
            { acupointName: '合谷', errorCount: 234, errorRate: 0.015 },
            { acupointName: '足三里', errorCount: 189, errorRate: 0.012 },
            { acupointName: '百会', errorCount: 156, errorRate: 0.01 }
          ],
          timeRange: {
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString()
          }
        },
        apiLatency: {
          averageLatency: 1250,
          p50Latency: 980,
          p95Latency: 2100,
          p99Latency: 3500,
          totalRequests: 45678,
          errorRate: 0.023,
          timeRange: {
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString()
          }
        },
        lastUpdated: new Date().toISOString()
      }
      
      setData(mockData)
    } catch (err) {
      setError('获取数据失败')
      console.error('获取仪表板数据失败:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-500">{error || '数据加载失败'}</p>
          <button 
            onClick={fetchDashboardData}
            className="btn-primary mt-4"
          >
            重试
          </button>
        </div>
      </div>
    )
  }

  const funnelData = [
    { name: '拍照', value: data.funnelStats.photoTaken, rate: 100 },
    { name: 'AI请求', value: data.funnelStats.aiRequestSent, rate: data.funnelStats.conversionRates.photoToRequest * 100 },
    { name: 'AI响应', value: data.funnelStats.aiResponseReceived, rate: data.funnelStats.conversionRates.requestToResponse * 100 },
    { name: '保存记录', value: data.funnelStats.noteSaved, rate: data.funnelStats.conversionRates.responseToSave * 100 }
  ]


  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">仪表板</h1>
        <p className="text-gray-500">ACC AI助手系统概览</p>
      </div>

      {/* 关键指标卡片 */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">总用户数</dt>
                <dd className="text-lg font-medium text-gray-900">{data.userStats.totalUsers.toLocaleString()}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Activity className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">月活跃用户</dt>
                <dd className="text-lg font-medium text-gray-900">{data.userStats.mau.toLocaleString()}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">AI准确率</dt>
                <dd className="text-lg font-medium text-gray-900">{(data.aiPerformance.accuracyRate * 100).toFixed(1)}%</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">API错误率</dt>
                <dd className="text-lg font-medium text-gray-900">{(data.apiLatency.errorRate * 100).toFixed(2)}%</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* 图表区域 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 用户漏斗分析 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">用户转化漏斗</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={funnelData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip 
                formatter={(value: number, name: string) => [
                  `${value.toLocaleString()}`,
                  name === 'value' ? '用户数' : name
                ]}
              />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* API延迟分布 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">API延迟统计</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">平均延迟</span>
              <span className="text-sm font-medium">{data.apiLatency.averageLatency}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">P50延迟</span>
              <span className="text-sm font-medium">{data.apiLatency.p50Latency}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">P95延迟</span>
              <span className="text-sm font-medium">{data.apiLatency.p95Latency}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">P99延迟</span>
              <span className="text-sm font-medium">{data.apiLatency.p99Latency}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">总请求数</span>
              <span className="text-sm font-medium">{data.apiLatency.totalRequests.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* AI性能指标 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">AI性能指标</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">准确率</span>
              <span className="text-sm font-medium">{(data.aiPerformance.accuracyRate * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">召回率</span>
              <span className="text-sm font-medium">{(data.aiPerformance.recallRate * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">平均修正数</span>
              <span className="text-sm font-medium">{data.aiPerformance.averageCorrectionCount.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">总预测数</span>
              <span className="text-sm font-medium">{data.aiPerformance.totalPredictions.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* 常见误识别穴位 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">常见误识别穴位</h3>
          <div className="space-y-3">
            {data.aiPerformance.commonMisidentifications.slice(0, 5).map((item, index) => (
              <div key={item.acupointName} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 w-8">{index + 1}.</span>
                  <span className="text-sm text-gray-700">{item.acupointName}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{item.errorCount}</div>
                  <div className="text-xs text-gray-500">{(item.errorRate * 100).toFixed(2)}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 最后更新时间 */}
      <div className="text-center text-sm text-gray-500">
        最后更新: {new Date(data.lastUpdated).toLocaleString('zh-CN')}
      </div>
    </div>
  )
}

export default Dashboard